import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, Cookie } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';

const CONSENT_KEY = 'unlocked_cookie_consent';

export type CookieConsent = 'accepted' | 'declined' | null;

export function getCookieConsent(): CookieConsent {
  try {
    return (localStorage.getItem(CONSENT_KEY) as CookieConsent) ?? null;
  } catch {
    return null;
  }
}

export function setCookieConsent(value: 'accepted' | 'declined') {
  try {
    localStorage.setItem(CONSENT_KEY, value);
    // Dispatch event so other components can react (e.g. analytics loader)
    window.dispatchEvent(new CustomEvent('cookie-consent-change', { detail: value }));
  } catch {
    // localStorage unavailable — silent fail
  }
}

export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    // Only show if no prior choice
    if (getCookieConsent() === null) {
      // Slight delay so it doesn't flash immediately on first paint
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  const dismiss = (choice: 'accepted' | 'declined') => {
    setCookieConsent(choice);
    setLeaving(true);
    setTimeout(() => setVisible(false), 350);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={leaving ? { y: 100, opacity: 0 } : { y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          role="dialog"
          aria-label="Cookie consent"
          aria-live="polite"
          className={cn(
            "fixed bottom-4 left-4 right-4 z-[9999] mx-auto max-w-2xl",
            "rounded-2xl shadow-2xl",
            // Navy background using CSS variable
            "bg-primary text-primary-foreground"
          )}
        >
          {/* Gold top accent line */}
          <div className="h-1 w-full rounded-t-2xl bg-accent" />

          <div className="p-5">
            <div className="flex items-start gap-3">
              {/* Icon */}
              <div className="shrink-0 mt-0.5 p-2 rounded-lg bg-accent/15">
                <Cookie className="w-4 h-4 text-accent" aria-hidden="true" />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium mb-1">
                  We use cookies to improve your experience and analyse usage.
                </p>
                <p className="text-xs text-primary-foreground/70 leading-relaxed">
                  Essential cookies keep the platform running. Optional analytics cookies help us understand how students use UnlockEd so we can make it better.{' '}
                  <Link
                    to="/cookies"
                    className="underline underline-offset-2 text-accent hover:text-accent/80 transition-colors"
                  >
                    Cookie Policy
                  </Link>
                  {' '}·{' '}
                  <Link
                    to="/privacy"
                    className="underline underline-offset-2 text-accent hover:text-accent/80 transition-colors"
                  >
                    Privacy Policy
                  </Link>
                </p>
              </div>

              {/* Close without choosing — leaves consent as null so banner reappears next visit */}
              <button
                onClick={() => { setLeaving(true); setTimeout(() => setVisible(false), 350); }}
                aria-label="Dismiss cookie banner"
                className="shrink-0 p-1 rounded-lg text-primary-foreground/50 hover:text-primary-foreground hover:bg-primary-foreground/10 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Action buttons */}
            <div className="flex flex-wrap gap-2 mt-4 justify-end">
              <button
                onClick={() => dismiss('declined')}
                className={cn(
                  "px-4 py-2 rounded-lg text-sm font-medium transition-all",
                  "border border-primary-foreground/25 text-primary-foreground/80",
                  "hover:bg-primary-foreground/10 hover:border-primary-foreground/50",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                )}
              >
                Essential only
              </button>
              <button
                onClick={() => dismiss('accepted')}
                className={cn(
                  "px-4 py-2 rounded-lg text-sm font-semibold transition-all",
                  "bg-accent text-accent-foreground",
                  "hover:bg-accent/90 active:scale-[0.98]",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-primary"
                )}
              >
                Accept all cookies
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
